import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { BUY, createOrder, selectors } from 'state';
import { formatters } from 'services';
import {
  estimatedControlledExposure,
  isSimplifiedOptionSettlementFailed,
  simplifiedOptionCurrencySymbol,
  simplifiedOptionActionLabel,
  simplifiedOptionActions,
} from '../../../state/app/simplified_options/presentation';
import type { SimplifiedOptionAction } from '../../../state/app/simplified_options/presentation';
import { useSimplifiedOptionMaxContracts } from './useSimplifiedOptionMaxContracts';
import {
  notionalCapacityStatus,
  resolveNotionalMaximum,
  useNotionalCapacity,
} from '../useNotionalCapacity';

const { TextArea } = Input;

type Props = {
  visible: boolean;
  hide: () => void;
  id: number;
};

/** Provides the class-19 market-only contract request without exposing controlled ledger units. */
const SimplifiedOptionOrderModal: React.FC<Props> = ({ visible, hide, id }) => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const instrument = useSelector(selectors.financialInstrumentById(id));
  const asset = useSelector(selectors.underlyingAssetOfInstrument(instrument));
  const currencies = useSelector(selectors.currencies);
  const maxContracts = useSimplifiedOptionMaxContracts(id);
  const option = instrument?.simplified_option;
  const [contracts, setContracts] = useState(1);
  const [transaction, setTransaction] = useState(BUY);
  const [investmentRationale, setInvestmentRationale] = useState('');

  const positionContracts = option?.contracts ?? 0;
  const notionalCapacity = useNotionalCapacity(id, transaction, visible);
  // An older backend omits the new flag, so only an explicit false removes
  // actions and rolling deployments preserve the existing order experience.
  const allowOptionSelling = session.allow_simplified_option_writing !== false;
  const allowShortSell =
    allowOptionSelling &&
    session.allow_short_sell &&
    !session.allow_short_only_on_fx;
  const actions = useMemo(
    () =>
      simplifiedOptionActions(
        positionContracts,
        allowOptionSelling,
        allowShortSell,
      ),
    [positionContracts, allowOptionSelling, allowShortSell],
  );
  useEffect(() => {
    if (!actions.some(action => action.transaction === transaction)) {
      setTransaction(actions[0]?.transaction ?? BUY);
    }
  }, [actions, transaction]);

  useEffect(() => {
    // A newly selected instrument starts at one contract; changing the trade
    // direction keeps the quantity the player has already entered.
    setContracts(1);
  }, [id]);

  if (!instrument || !asset || !option) return null;

  const multiplier =
    option.contract_multiplier || asset.contract_multiplier || 1;
  const controlledAmount = contracts * multiplier;
  const nativeCurrency = simplifiedOptionCurrencySymbol(
    option.native_currency || asset.variable_currency,
    currencies,
  );
  const normalizedSpot =
    asset.underlying_kind === 'BOND'
      ? undefined
      : option.normalized_spot == null
        ? undefined
        : Number(option.normalized_spot);
  const controlledExposure = estimatedControlledExposure(
    contracts,
    multiplier,
    asset.underlying_kind,
    normalizedSpot,
  );
  const absolutePremium =
    option.premium_per_controlled_unit == null
      ? null
      : Number(option.premium_per_controlled_unit) * controlledAmount;
  const signedPremium =
    absolutePremium == null
      ? null
      : transaction === BUY
        ? -absolutePremium
        : absolutePremium;
  const maximumContracts = resolveNotionalMaximum({
    localMaximum: maxContracts[transaction] ?? 0,
    capacity: notionalCapacity,
    transaction,
    signedPosition: positionContracts,
    contracts: true,
  });
  const capacityStatus = notionalCapacityStatus(notionalCapacity);
  const v2CapacityApplied =
    notionalCapacity.usesBackend && notionalCapacity.result?.applies !== false;
  const rationaleSatisfied =
    !session.require_investment_rationale || investmentRationale.length >= 10;
  const settlementFailed = isSimplifiedOptionSettlementFailed(option);
  const available =
    option.availability?.available === true && !settlementFailed;
  const invalidContracts =
    !Number.isInteger(contracts) ||
    contracts <= 0 ||
    contracts > maximumContracts;

  return (
    <Modal
      open={visible}
      title={`Trade ${asset.name || asset.ticker}`}
      onCancel={hide}
      footer={[
        <Button key="cancel" onClick={hide}>
          Cancel
        </Button>,
        <Button
          key="place"
          type="primary"
          disabled={!available || invalidContracts || !rationaleSatisfied}
          onClick={() => {
            dispatch(
              createOrder({
                resourcetype: 'MarketOrder',
                financial_instrument: instrument.id,
                transaction,
                contracts,
                forecast: 0,
                limit_price: 0,
                investment_rationale: investmentRationale || undefined,
                hedge: false,
              }),
            );
            hide();
          }}
        >
          Place market order
        </Button>,
      ]}
    >
      {!available && (
        <Alert
          type="warning"
          showIcon
          message={
            settlementFailed
              ? 'Settlement failed; trading is quarantined.'
              : option.availability?.message || 'This option is unavailable.'
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 12 }}>
        Action:{' '}
        <Radio.Group
          value={transaction}
          onChange={event => setTransaction(event.target.value)}
        >
          {actions.map((action: SimplifiedOptionAction) => (
            <Radio key={action.transaction} value={action.transaction}>
              {simplifiedOptionActionLabel(action, contracts)}
            </Radio>
          ))}
        </Radio.Group>
      </div>

      <div style={{ marginBottom: 12 }}>
        Contracts:{' '}
        <InputNumber
          aria-label="Contracts"
          min={1}
          max={maximumContracts}
          step={1}
          precision={0}
          value={contracts}
          formatter={formatters.commasNoDigits}
          onChange={value => setContracts(Number(value) || 0)}
        />
        {` Max ${formatters.commasNoDigits(maximumContracts)}`}
        <div style={{ color: '#8c8c8c', marginTop: 4 }}>
          1 contract controls {formatters.commasNoDigits(multiplier)} units.
        </div>
      </div>

      <Card size="small" title="Order summary">
        <Row gutter={[12, 12]}>
          <Col span={12}>
            Current strike
            <br />
            {option.current_strike == null
              ? '-'
              : formatters.commas2Digits(option.current_strike)}
          </Col>
          <Col span={12}>
            Expiry
            <br />
            {option.days_to_expiry == null
              ? '-'
              : `${option.days_to_expiry} days`}
          </Col>
          <Col span={12}>
            Estimated premium
            <br />
            {signedPremium == null
              ? '-'
              : `${formatters.commas2Digits(signedPremium)} ${nativeCurrency}`}
          </Col>
          <Col span={12}>
            {v2CapacityApplied ? 'V2 notional limit' : 'Notional'}
            <br />
            {v2CapacityApplied
              ? notionalCapacity.result?.configured_limit_reporting == null
                ? '-'
                : `${formatters.commas2Digits(
                    Number(notionalCapacity.result.configured_limit_reporting),
                  )} ${currencies[session.reporting_currency]?.symbol ?? ''}`
              : controlledExposure == null
                ? '-'
                : `${formatters.commasNoDigits(controlledExposure)} ${
                    nativeCurrency
                  }`}
          </Col>
        </Row>
        {v2CapacityApplied && capacityStatus ? (
          <div style={{ color: '#8c8c8c', marginTop: 8 }}>{capacityStatus}</div>
        ) : null}
      </Card>

      <TextArea
        style={{ marginTop: 12 }}
        maxLength={511}
        minLength={session.require_investment_rationale ? 10 : undefined}
        placeholder={
          session.require_investment_rationale
            ? 'Investment rationale (minimum 10 characters)'
            : 'Investment rationale'
        }
        value={investmentRationale}
        onChange={event => setInvestmentRationale(event.target.value)}
      />
    </Modal>
  );
};

export default SimplifiedOptionOrderModal;
